import { parseMoneyCents, statedAmounts } from "./figures";
import { salesMedian } from "./salesMeasure";

/**
 * F-11, `pnpm measure:vip-pricing`: whether VIP and non-VIP customers were
 * quoted differently, as far as the rows can show. Pure function, no IO, the
 * split salesMeasure.ts uses: this takes rows the loader read
 * (`packages/db/src/vipPricingEvidence.ts`) and does every piece of
 * arithmetic in deterministic code (CLAUDE.md section 4).
 *
 * THE RULE IT AUDITS. CLAUDE.md section 3 (T3, permanent), Master Spec B2 and
 * Agent Brains 2.3 #12: VIP status changes service level and routing, never a
 * number. FINDINGS F-11: a VIP quoted the KB's premium option while a
 * non-VIP is quoted the entry option is fully grounded and passes every gate,
 * so the risk is only detectable in aggregate, never per message. The vault's
 * ruling (Q35, F-11): "vip_flag logged with every quoted option, standing
 * monthly price-distribution-by-VIP audit".
 *
 * REPORT ONLY, AND NO VERDICT. This puts the two groups side by side. Nothing
 * here decides whether a difference is a breach: a difference is a question
 * for a human, not proof, and no field is named for a verdict.
 *
 * THE FLAG IS READ NOW. The contact's `vip_flag` as it reads when the report
 * runs, because no snapshot is stored on the draft. A contact flagged after
 * the reply was composed lands in the VIP group. The recommendation for it is
 * RATIFIED by Kabir 2026-10-02 (decision list item 160, typed reply "Yes I
 * want to continue the production" to the accept-the-recs question): no
 * migration now, and when item 147's next migration (`goal_step`) is written,
 * the VIP tag as it stood at compose time is added to the draft row in the
 * same file, and this report then reads that instead of the tag now.
 *
 * WHAT A PRICED FACT IS. A KB fact whose text states money, the definition
 * `kbFreshness` (freshness.ts) counts as priced, read by `statedAmounts`
 * (F-16). The KB carries no price field: `KbChunk.fact` is text.
 *
 * WHERE THE OPTIONS LIVE. Every KB producer keeps one chunk per topic: stage
 * 4 builds `kb_<topic>`, the owner interview holds a same-subject fact rather
 * than add a second chunk, and kbFromInterview.ts refuses a repeated topic.
 * So a premium and an entry option (natural against lab grown, platinum
 * against gold) sit inside ONE fact that lists both amounts. A topic's
 * options are every amount its priced facts state, and a draft's option is
 * known two ways: it cites a fact stating one amount (the citation is the
 * choice), or it cites a fact listing several and the text that left states
 * one of them (`facts_used` names the chunk, the text names the amount).
 * Cited with none of its amounts stated, no option is known, and that is
 * counted apart. The text's amounts are matched against the cited fact's own
 * amounts only, so an amount from another topic never ranks this one.
 *
 * THE UNIT IS THE CUSTOMER. A wealth-pricing difference is between people,
 * and a VIP custom order is a long thread: two VIPs can write thirty drafts.
 * The floor counts distinct customers, and each count a comparison rests on
 * carries the customers behind it. The loader hands an opaque per-run ordinal for the
 * contact, never its id.
 */

/**
 * The fewest distinct customers per group before a share or a median is
 * printed.
 *
 * RATIFIED by Kabir 2026-10-02 (decision list item 160, typed reply "Yes I
 * want to continue the production" to the accept-the-recs question), at 30.
 * The spec names no number. 30 is the conservative reading: below it a share
 * moves by more than three points per customer, and the report says "too few
 * to compare" rather than print one. It counts customers, not drafts (a
 * customer has at least one draft, so it is never fewer than 30 drafts
 * either), and it binds BOTH groups: a figure for one side with nothing beside
 * it is not a comparison.
 *
 * The report stays report only for the pilot (no verdict, no threshold, no
 * exit code), run monthly by hand with `--since` covering the month until a
 * scheduler exists. One shop will not reach 30 VIP customers in a month, so a
 * gate would never fire or would fire on noise; making it a gate is not part
 * of this ruling.
 */
export const VIP_PRICING_MIN_PER_GROUP = 30;

/** How a KB fact carries a price, as the figures module reads its text. */
export type FactPrice =
  | { readonly kind: "unpriced" }
  /** Exactly one amount, read whole, possibly written more than once. */
  | { readonly kind: "one"; readonly cents: number }
  /** Two or more different amounts, each once, lowest first: the options one fact lists. */
  | { readonly kind: "several"; readonly cents: readonly number[] }
  /** A money-like token the figures module will not read whole ("$8k", "$8.5"). */
  | { readonly kind: "unreadable" };

/**
 * A KB fact's price. Money only (section 4's "only money perishes" reading
 * in freshness.ts): no turnaround, carat or percent is a price here.
 */
export function factPrice(fact: string): FactPrice {
  const stated = statedAmounts(fact);
  if (stated.unreadable.length > 0) return { kind: "unreadable" };
  const distinct = [...new Set(stated.amounts.map(wholeCents))].sort((a, b) => a - b);
  const only = distinct.length === 1 ? distinct[0] : undefined;
  if (only !== undefined) return { kind: "one", cents: only };
  return distinct.length === 0 ? { kind: "unpriced" } : { kind: "several", cents: distinct };
}

/** The amounts a fact's price states: none for unpriced or unreadable. */
function amountsOf(price: FactPrice): readonly number[] {
  if (price.kind === "one") return [price.cents];
  return price.kind === "several" ? price.cents : [];
}

/** A token `statedAmounts` read whole, as cents. Its own refusal is never reached and never swallowed. */
function wholeCents(token: string): number {
  const cents = parseMoneyCents(token);
  if (cents === null) throw new Error("the figures module read an amount whole and then could not read it");
  return cents;
}

export interface QuotedAmounts {
  /** Each distinct amount the text states, in integer cents, in the order first written. */
  readonly cents: readonly number[];
  /** Money-like tokens the figures module will not read whole: counted, never read as part of a price. */
  readonly unreadable: number;
}

/**
 * The money one transmitted text states. The loader hands the text here and
 * keeps only what this returns: numbers, never words (CLAUDE.md section 10).
 * Dollars written with "$" only, `statedAmounts`' reach: "85 dollars" and
 * "USD 70" are not read (listed for Kabir).
 */
export function quotedAmounts(text: string): QuotedAmounts {
  const stated = statedAmounts(text);
  return { cents: [...new Set(stated.amounts.map(wholeCents))], unreadable: stated.unreadable.length };
}

/** Which KB version the draft was composed against, from its `artifact_versions` stamp. */
export type VipPricingKbRef =
  | { readonly state: "resolved"; readonly version: number }
  /** No stamp: a draft written before migration 0033, or by a run with no durable store. */
  | { readonly state: "not_recorded" }
  /** A stamp this code did not write. */
  | { readonly state: "unreadable" }
  /** The stamp names a version, or a content hash, the store does not hold for this client. */
  | { readonly state: "not_in_store" };

/** One draft that left the building in the period (F-06: `transmitted_at` set). */
export interface VipPricingDraftRow {
  /** The contact's `vip_flag`, read now. */
  readonly vip: boolean;
  /** The contact, as an opaque ordinal for this read only: equal for one contact, never its id. */
  readonly customer: number;
  /** `facts_used`, as stored. */
  readonly factsUsed: readonly string[];
  readonly kb: VipPricingKbRef;
  /** `quotedAmounts` on the text that left. */
  readonly quotedCents: readonly number[];
  readonly unreadableAmounts: number;
}

export interface VipPricingKbFact {
  readonly factId: string;
  readonly topic: string;
  readonly price: FactPrice;
}

/** One KB version a draft in the period names, its facts reduced to their prices. */
export interface VipPricingKbVersion {
  readonly version: number;
  readonly facts: readonly VipPricingKbFact[];
}

export interface VipPricingInput {
  readonly transmitted: readonly VipPricingDraftRow[];
  /** Released (approved or sent) in the period and not recorded as transmitted: left out, and counted. */
  readonly releasedNotTransmitted: number;
  /** Of those, the ones whose send claim is uncertain or still sending: they may have reached the customer. */
  readonly releasedMayHaveLeft: number;
  readonly kb: readonly VipPricingKbVersion[];
}

export interface VipPricingAmounts {
  /** Distinct amounts per draft, summed over the group's drafts. */
  readonly count: number;
  readonly draftsStating: number;
  /** Distinct customers behind `draftsStating`: the floor counts these. */
  readonly customersStating: number;
  readonly unreadable: number;
  /** All three are null unless both groups meet the floor. The median is the upper middle. */
  readonly medianCents: number | null;
  readonly minCents: number | null;
  readonly maxCents: number | null;
}

export interface VipPricingGroup {
  readonly transmitted: number;
  /** Distinct customers behind `transmitted`. */
  readonly customers: number;
  readonly kbNotRecorded: number;
  readonly kbUnreadable: number;
  readonly kbNotInStore: number;
  /** Drafts citing at least one priced fact of their own KB version. */
  readonly citingPricedFact: number;
  readonly amounts: VipPricingAmounts;
}

export interface VipPricingFact {
  readonly factId: string;
  /** Its price in each KB version a citing draft named, oldest version first. */
  readonly prices: readonly { readonly version: number; readonly price: FactPrice }[];
  /** Drafts citing it, per group, and the customers behind them. Counts, never a share. */
  readonly vip: number;
  readonly notVip: number;
  readonly vipCustomers: number;
  readonly notVipCustomers: number;
}

export interface VipPricingOption {
  /** More than one when two options tie at that price. */
  readonly factIds: readonly string[];
  readonly cents: number;
}

export interface VipPricingTopicSide {
  /** Drafts in which an option of the topic is known (see WHERE THE OPTIONS LIVE), and their customers. */
  readonly drafts: number;
  readonly customers: number;
  /** Drafts whose known option is the topic's highest amount; one with both ends counts in both. */
  readonly highest: number;
  readonly highestCustomers: number;
  readonly lowest: number;
  readonly lowestCustomers: number;
  /** Drafts citing a fact of the topic that lists several amounts, with none of them stated in the text. */
  readonly noOptionStated: number;
  /** Drafts citing an option of the topic whose amount the figures module will not read whole. */
  readonly unranked: number;
  /** Null unless both groups meet the floor. A share of drafts, beside the customers behind it. */
  readonly highestPercent: number | null;
  readonly lowestPercent: number | null;
}

export interface VipPricingTopic {
  readonly topic: string;
  /** Per KB version where the topic has two options apart in price: its two ends. */
  readonly versions: readonly {
    readonly version: number;
    readonly highest: VipPricingOption;
    readonly lowest: VipPricingOption;
  }[];
  readonly vip: VipPricingTopicSide;
  readonly notVip: VipPricingTopicSide;
  /** Both sides' customers at or over the floor. */
  readonly compared: boolean;
}

export interface VipPricingMeasure {
  readonly minPerGroup: number;
  readonly releasedNotTransmitted: number;
  readonly releasedMayHaveLeft: number;
  readonly vip: VipPricingGroup;
  readonly notVip: VipPricingGroup;
  readonly amountsCompared: boolean;
  /** Priced facts cited by at least one draft, by fact id. */
  readonly facts: readonly VipPricingFact[];
  /** Topics with two amounts apart in some KB version a draft named, by topic, cited or not. */
  readonly topics: readonly VipPricingTopic[];
  /** Cited ids absent from the KB version the citing draft names. */
  readonly citedNotInKb: number;
}

type Side = "vip" | "notVip";

/** One topic's ranked ends in one KB version, or null when its amounts are fewer than two apart. */
interface Ends {
  readonly highest: VipPricingOption;
  readonly lowest: VipPricingOption;
}

function endsOf(facts: readonly VipPricingKbFact[]): Ends | null {
  const all = facts.flatMap((f) => amountsOf(f.price));
  if (all.length === 0) return null;
  const max = Math.max(...all);
  const min = Math.min(...all);
  if (max === min) return null;
  const at = (cents: number): VipPricingOption => ({
    factIds: facts.filter((f) => amountsOf(f.price).includes(cents)).map((f) => f.factId).sort(),
    cents,
  });
  return { highest: at(max), lowest: at(min) };
}

function assertDraft(d: VipPricingDraftRow): void {
  if (!Number.isSafeInteger(d.customer)) throw new Error(`a draft's customer is not a whole number: ${d.customer}`);
  for (const v of d.quotedCents) {
    if (!Number.isSafeInteger(v) || v < 0) throw new Error(`a quoted amount is not whole cents: ${v}`);
  }
}

/** A count of drafts beside the set of customers behind it. */
interface Tally {
  drafts: number;
  readonly customers: Set<number>;
}

const emptyTally = (): Tally => ({ drafts: 0, customers: new Set() });

function add(t: Tally, customer: number): void {
  t.drafts += 1;
  t.customers.add(customer);
}

interface TopicTally {
  readonly known: Tally;
  readonly highest: Tally;
  readonly lowest: Tally;
  noOptionStated: number;
  unranked: number;
}

const emptyTopicTally = (): TopicTally => ({
  known: emptyTally(),
  highest: emptyTally(),
  lowest: emptyTally(),
  noOptionStated: 0,
  unranked: 0,
});

/** What one draft's citations say about one topic. */
interface TopicTouch {
  /** The amounts of the topic this draft is known to have quoted. */
  readonly chosen: Set<number>;
  listedNoneStated: boolean;
  unreadable: boolean;
}

function percent(n: number, of: number): number {
  return (100 * n) / of;
}

export function measureVipPricing(input: VipPricingInput, minPerGroup: number = VIP_PRICING_MIN_PER_GROUP): VipPricingMeasure {
  const versions = new Map<number, Map<string, VipPricingKbFact>>();
  const endsByVersion = new Map<number, Map<string, Ends>>();
  for (const kb of input.kb) {
    if (versions.has(kb.version)) throw new Error(`KB version ${kb.version} was read twice`);
    versions.set(kb.version, new Map(kb.facts.map((f) => [f.factId, f])));
    const byTopic = new Map<string, VipPricingKbFact[]>();
    for (const f of kb.facts) byTopic.set(f.topic, [...(byTopic.get(f.topic) ?? []), f]);
    const ends = new Map<string, Ends>();
    for (const [topic, facts] of byTopic) {
      const e = endsOf(facts);
      if (e !== null) ends.set(topic, e);
    }
    endsByVersion.set(kb.version, ends);
  }

  const emptyGroup = () => ({
    transmitted: emptyTally(),
    stating: emptyTally(),
    kbNotRecorded: 0,
    kbUnreadable: 0,
    kbNotInStore: 0,
    citingPricedFact: 0,
    cents: [] as number[],
    unreadable: 0,
  });
  const groups = { vip: emptyGroup(), notVip: emptyGroup() };
  const facts = new Map<string, { prices: Map<number, FactPrice>; vip: Tally; notVip: Tally }>();
  const topics = new Map<string, { vip: TopicTally; notVip: TopicTally }>();
  let citedNotInKb = 0;

  for (const d of input.transmitted) {
    assertDraft(d);
    const side: Side = d.vip ? "vip" : "notVip";
    const g = groups[side];
    add(g.transmitted, d.customer);
    g.cents.push(...d.quotedCents);
    if (d.quotedCents.length > 0) add(g.stating, d.customer);
    g.unreadable += d.unreadableAmounts;

    let kb: Map<string, VipPricingKbFact> | undefined;
    if (d.kb.state === "not_recorded") g.kbNotRecorded += 1;
    else if (d.kb.state === "unreadable") g.kbUnreadable += 1;
    else if (d.kb.state === "not_in_store") g.kbNotInStore += 1;
    else {
      kb = versions.get(d.kb.version);
      // A stamp naming a version the loader did not hand over is the store not holding it.
      if (kb === undefined) g.kbNotInStore += 1;
    }
    if (kb === undefined || d.kb.state !== "resolved") continue;
    const version = d.kb.version;
    const ends = endsByVersion.get(version) ?? new Map<string, Ends>();
    const quoted = new Set(d.quotedCents);

    let citedPriced = false;
    const touched = new Map<string, TopicTouch>();
    for (const id of new Set(d.factsUsed)) {
      const fact = kb.get(id);
      if (fact === undefined) {
        citedNotInKb += 1;
        continue;
      }
      if (fact.price.kind === "unpriced") continue;
      citedPriced = true;
      const row = facts.get(id) ?? { prices: new Map<number, FactPrice>(), vip: emptyTally(), notVip: emptyTally() };
      row.prices.set(version, fact.price);
      add(row[side], d.customer);
      facts.set(id, row);

      if (!ends.has(fact.topic)) continue;
      const t = touched.get(fact.topic) ?? { chosen: new Set<number>(), listedNoneStated: false, unreadable: false };
      if (fact.price.kind === "one") {
        // A fact stating one amount: citing it is choosing it.
        t.chosen.add(fact.price.cents);
      } else if (fact.price.kind === "several") {
        // A fact listing several: the text that left says which, or nothing does.
        const stated = fact.price.cents.filter((c) => quoted.has(c));
        for (const c of stated) t.chosen.add(c);
        if (stated.length === 0) t.listedNoneStated = true;
      } else {
        t.unreadable = true;
      }
      touched.set(fact.topic, t);
    }
    if (citedPriced) g.citingPricedFact += 1;
    for (const [topic, t] of touched) {
      const e = ends.get(topic);
      if (e === undefined) continue;
      const row = topics.get(topic) ?? { vip: emptyTopicTally(), notVip: emptyTopicTally() };
      const tally = row[side];
      if (t.chosen.size > 0) {
        add(tally.known, d.customer);
        if (t.chosen.has(e.highest.cents)) add(tally.highest, d.customer);
        if (t.chosen.has(e.lowest.cents)) add(tally.lowest, d.customer);
      } else if (t.listedNoneStated) {
        tally.noOptionStated += 1;
      }
      if (t.unreadable) tally.unranked += 1;
      topics.set(topic, row);
    }
  }

  const amountsCompared =
    groups.vip.stating.customers.size >= minPerGroup && groups.notVip.stating.customers.size >= minPerGroup;
  const group = (s: Side): VipPricingGroup => {
    const g = groups[s];
    const sorted = [...g.cents].sort((a, b) => a - b);
    return {
      transmitted: g.transmitted.drafts,
      customers: g.transmitted.customers.size,
      kbNotRecorded: g.kbNotRecorded,
      kbUnreadable: g.kbUnreadable,
      kbNotInStore: g.kbNotInStore,
      citingPricedFact: g.citingPricedFact,
      amounts: {
        count: g.cents.length,
        draftsStating: g.stating.drafts,
        customersStating: g.stating.customers.size,
        unreadable: g.unreadable,
        medianCents: amountsCompared ? salesMedian(sorted) : null,
        minCents: amountsCompared ? (sorted[0] ?? null) : null,
        maxCents: amountsCompared ? (sorted[sorted.length - 1] ?? null) : null,
      },
    };
  };

  // Every rankable topic of a version the loader handed over is listed, cited or not: a zero is a count too.
  for (const ends of endsByVersion.values()) {
    for (const topic of ends.keys()) if (!topics.has(topic)) topics.set(topic, { vip: emptyTopicTally(), notVip: emptyTopicTally() });
  }
  const topicRows: VipPricingTopic[] = [...topics.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([topic, row]) => {
      const compared = row.vip.known.customers.size >= minPerGroup && row.notVip.known.customers.size >= minPerGroup;
      const side = (t: TopicTally): VipPricingTopicSide => ({
        drafts: t.known.drafts,
        customers: t.known.customers.size,
        highest: t.highest.drafts,
        highestCustomers: t.highest.customers.size,
        lowest: t.lowest.drafts,
        lowestCustomers: t.lowest.customers.size,
        noOptionStated: t.noOptionStated,
        unranked: t.unranked,
        highestPercent: compared ? percent(t.highest.drafts, t.known.drafts) : null,
        lowestPercent: compared ? percent(t.lowest.drafts, t.known.drafts) : null,
      });
      const perVersion = [...endsByVersion.entries()]
        .flatMap(([version, ends]) => {
          const e = ends.get(topic);
          return e === undefined ? [] : [{ version, highest: e.highest, lowest: e.lowest }];
        })
        .sort((a, b) => a.version - b.version);
      return { topic, versions: perVersion, vip: side(row.vip), notVip: side(row.notVip), compared };
    });

  return {
    minPerGroup,
    releasedNotTransmitted: input.releasedNotTransmitted,
    releasedMayHaveLeft: input.releasedMayHaveLeft,
    vip: group("vip"),
    notVip: group("notVip"),
    amountsCompared,
    facts: [...facts.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([factId, row]) => ({
        factId,
        prices: [...row.prices.entries()].sort(([a], [b]) => a - b).map(([version, price]) => ({ version, price })),
        vip: row.vip.drafts,
        notVip: row.notVip.drafts,
        vipCustomers: row.vip.customers.size,
        notVipCustomers: row.notVip.customers.size,
      })),
    topics: topicRows,
    citedNotInKb,
  };
}
